import { withAuthenticationRequired } from "react-oidc-context";
import { Outlet } from "@tanstack/react-router";
import DashboardRoot from "@/src/components/templates/Dashboard/Dashboard";
import ErrorRoot from "@/src/components/templates/Error/Error";
import CreateValidatedQuestion from "@/src/components/pages/CreateValidatedQuestion";
import ValidatedQuestionsBySubjectDidOverview from "@/src/components/pages/ValidatedQuestionsBySubjectDidOverview";
import CreateHealthcareProviderExcerpt from "@/src/components/pages/CreateHealthcareProviderExcerpt";
import HealthcareProviderExcerptOverview from "@/src/components/pages/HealthcareProviderExcerptOverview";
import SubjectDIDsOverview from "@/src/components/pages/SubjectDIDsOverview";
import UsersOverview from "@/src/components/pages/UsersOverview";
import CreateUser from "@/src/components/pages/CreateUser";
import EditSubjectDID from "@/src/components/pages/EditSubjectDID";
import {
    createRootRoute,
    createRoute,
    createRouter,
    Navigate,
} from "@tanstack/react-router";
import Devtools from "./Devtools";
import ValidatedQuestionDetail from "@/src/components/pages/validated-question-detail";
import UserDetail from "@/src/components/pages/UserDetail";
import { useDashboardConfig } from "./useDashboardConfig";
import RolesOverview from "@/src/components/pages/RolesOverview";
import CreateRole from "@/src/components/pages/CreateRole";
import RoleDetail from "@/src/components/pages/RoleDetail";
import { ProtectedRoute } from "@/src/components/molecules/ProtectedRoute/ProtectedRoute";
import { usePermissions } from "@/src/lib/usePermissions/usePermissions";

const rootRoute = createRootRoute({
    component: function Root() {
        return (
            <>
                <Outlet />
                <Devtools />
            </>
        );
    },
});

const mainLayoutRoute = createRoute({
    getParentRoute: () => rootRoute,
    id: "main-layout",
    component: function MainLayout() {
        const Dashboard = withAuthenticationRequired(DashboardRoot);
        const { mainMenuItems, accountMenuItems } = useDashboardConfig("main");

        return (
            <Dashboard
                mainMenuItems={mainMenuItems}
                accountMenuItems={accountMenuItems}
            />
        );
    },
});

const slashRoute = createRoute({
    getParentRoute: () => mainLayoutRoute,
    path: "/",
    component: function Slash() {
        return <Navigate to="/validated-questions" />;
    },
});

const validatedQuestionsRoute = createRoute({
    getParentRoute: () => mainLayoutRoute,
    path: "/validated-questions",
    component: () => (
        <ProtectedRoute requiredPermission="VERIFIABLE_QUERY_CREDENTIAL_READ">
            <ValidatedQuestionsBySubjectDidOverview />
        </ProtectedRoute>
    ),
});

const newValidatedQuestionRoute = createRoute({
    getParentRoute: () => mainLayoutRoute,
    path: "/validated-questions/new",
    component: () => (
        <ProtectedRoute requiredPermission="VERIFIABLE_QUERY_CREDENTIAL_CREATE">
            <CreateValidatedQuestion />
        </ProtectedRoute>
    ),
});

export const ValidatedQuestionDetailRoute = createRoute({
    getParentRoute: () => mainLayoutRoute,
    path: "/validated-questions/$id",
    component: () => (
        <ProtectedRoute requiredPermission="VERIFIABLE_QUERY_CREDENTIAL_READ">
            <ValidatedQuestionDetail />
        </ProtectedRoute>
    ),
});

const healthcareProviderExcerptsRoute = createRoute({
    getParentRoute: () => mainLayoutRoute,
    path: "/healthcare-provider-excerpts",
    component: () => (
        <ProtectedRoute requiredPermission="HEALTHCARE_PROVIDER_EXCERPT_CREDENTIAL_READ">
            <HealthcareProviderExcerptOverview />
        </ProtectedRoute>
    ),
});

const newHealthcareProviderExcerptRoute = createRoute({
    getParentRoute: () => mainLayoutRoute,
    path: "/healthcare-provider-excerpts/new",
    component: () => (
        <ProtectedRoute requiredPermission="HEALTHCARE_PROVIDER_EXCERPT_CREDENTIAL_CREATE">
            <CreateHealthcareProviderExcerpt />
        </ProtectedRoute>
    ),
});

// Admin Routes

const adminRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/admin",
    component: function Admin() {
        const Dashboard = withAuthenticationRequired(DashboardRoot);
        const { mainMenuItems, accountMenuItems } = useDashboardConfig("admin");

        return (
            <Dashboard
                testId="admin-dashboard"
                mainMenuItems={mainMenuItems}
                accountMenuItems={accountMenuItems}
            />
        );
    },
});

const adminIndexRoute = createRoute({
    getParentRoute: () => adminRoute,
    path: "/",
    component: function AdminIndex() {
        const { hasPermission } = usePermissions();

        if (hasPermission("SUBJECT_DIDS_READ")) {
            return <Navigate to="/admin/subject-dids" />;
        } else if (hasPermission("USERS_READ")) {
            return <Navigate to="/admin/users" />;
        } else if (hasPermission("ROLES_READ")) {
            return <Navigate to="/admin/roles" />;
        } else {
            // Any route will do because the page rendered will show a no permissions message
            return <Navigate to="/admin/subject-dids" />;
        }
    },
});

const subjectDIDsRoute = createRoute({
    getParentRoute: () => adminRoute,
    path: "/subject-dids",
    component: () => (
        <ProtectedRoute requiredPermission="SUBJECT_DIDS_READ">
            <SubjectDIDsOverview />
        </ProtectedRoute>
    ),
});

export const editSubjectDIDRoute = createRoute({
    getParentRoute: () => adminRoute,
    path: "/subject-dids/$id",
    component: () => (
        <ProtectedRoute requiredPermission="SUBJECT_DIDS_UPDATE">
            <EditSubjectDID />
        </ProtectedRoute>
    ),
});

const usersRoute = createRoute({
    getParentRoute: () => adminRoute,
    path: "/users",
    component: () => (
        <ProtectedRoute requiredPermission="USERS_READ">
            <UsersOverview />
        </ProtectedRoute>
    ),
});

const newUserRoute = createRoute({
    getParentRoute: () => adminRoute,
    path: "/users/new",
    component: () => (
        <ProtectedRoute requiredPermission="USERS_CREATE">
            <CreateUser />
        </ProtectedRoute>
    ),
});

export const userDetailRoute = createRoute({
    getParentRoute: () => adminRoute,
    path: "/users/$id",
    component: () => (
        <ProtectedRoute requiredPermission="USERS_READ">
            <UserDetail />
        </ProtectedRoute>
    ),
});

const rolesRoute = createRoute({
    getParentRoute: () => adminRoute,
    path: "/roles",
    component: () => (
        <ProtectedRoute requiredPermission="ROLES_READ">
            <RolesOverview />
        </ProtectedRoute>
    ),
});

const newRoleRoute = createRoute({
    getParentRoute: () => adminRoute,
    path: "/roles/new",
    component: () => (
        <ProtectedRoute requiredPermission="ROLES_CREATE">
            <CreateRole />
        </ProtectedRoute>
    ),
});

const roleDetailRoute = createRoute({
    getParentRoute: () => adminRoute,
    path: "/roles/$id",
    component: () => (
        <ProtectedRoute requiredPermission="ROLES_UPDATE">
            <RoleDetail />
        </ProtectedRoute>
    ),
});

const starRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "*",
    component: ErrorRoot,
});

const routeTree = rootRoute.addChildren([
    mainLayoutRoute.addChildren([
        slashRoute,
        validatedQuestionsRoute,
        ValidatedQuestionDetailRoute,
        newValidatedQuestionRoute,
        healthcareProviderExcerptsRoute,
        newHealthcareProviderExcerptRoute,
    ]),
    adminRoute.addChildren([
        adminIndexRoute,
        subjectDIDsRoute,
        editSubjectDIDRoute,
        usersRoute,
        newUserRoute,
        userDetailRoute,
        rolesRoute,
        newRoleRoute,
        roleDetailRoute,
    ]),
    starRoute,
]);

export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
    interface Register {
        router: typeof router;
    }
}
